import React, { useState, useRef, useEffect } from 'react';
import { Plus, Minus, Circle, Paperclip, X } from 'lucide-react';
import { MESSAGE_TYPES, CATEGORIES } from '../../utils/constants';
import { categories } from '../../utils/textAnalysis';

const MessageCard = ({ 
  message, 
  index, 
  isHighlighted, 
  onToggleSentiment, 
  onUpdateCategory,
  onAddNote,
  onAddAttachment 
}) => {
  // State declarations
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showActions, setShowActions] = useState(false);
  const timeoutRef = useRef(null);
  
  // Refs
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Categories definition
  const allCategories = {
    ...categories,
    [CATEGORIES.DEFAULT]: [], // Personal Development
    [CATEGORIES.ALL]: [], // All
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      // Set a timeout to close the dropdown after 4 seconds
      const timer = setTimeout(() => {
        setIsDropdownOpen(false);
      }, 4000);

      // Cleanup function to clear the timeout
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.message-container')) {
        setShowActions(false);
        setShowNoteInput(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleToggleSentiment = () => {
    const newSentiment = {
      [MESSAGE_TYPES.POSITIVE]: MESSAGE_TYPES.NEUTRAL,
      [MESSAGE_TYPES.NEUTRAL]: MESSAGE_TYPES.NEGATIVE,
      [MESSAGE_TYPES.NEGATIVE]: MESSAGE_TYPES.POSITIVE,
    }[message.sentiment];

    onToggleSentiment(message.id, newSentiment);
  };

  const handleCategoryClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCategorySelect = (newCategory) => {
    onUpdateCategory(message.id, newCategory);
    setIsDropdownOpen(false);
  };

  const handleNoteSubmit = (e) => {
    e.preventDefault();
    if (noteText.trim()) {
      onAddNote(message.id, noteText);
      setNoteText('');
      setShowNoteInput(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      onAddAttachment(message.id, file);
      // Reset the input value to allow the same file to be selected again
      event.target.value = '';
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowActions(true);
  };

  const handleMouseLeave = () => {
    // Only start the timeout if we're not showing the note input
    if (!showNoteInput) {
      timeoutRef.current = setTimeout(() => {
        setShowActions(false);
      }, 500); // 0.5 second delay before hiding
    }
  };

  return (
    <div 
      id={`message-${index}`}
      className="message-container mb-4 p-4 bg-white rounded-lg shadow border transition-colors duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex justify-between items-start">
        <div className="flex-grow">
          <div className="text-sm text-gray-500 mb-1">
            {message.timestamp.toLocaleDateString()}
          </div>
          <p className="text-gray-800">{message.text}</p>
          <div className="relative text-xs text-blue-500 mt-1">
            <button 
              onClick={handleCategoryClick} 
              className="hover:text-blue-700 focus:outline-none"
            >
              {message.category}
            </button>
            {isDropdownOpen && (
              <div 
                ref={dropdownRef}
                className="absolute left-0 bg-white border rounded shadow-lg mt-1 z-10 min-w-[150px]"
              >
                {Object.keys(allCategories).map((category) => (
                  <div
                    key={category}
                    onClick={() => handleCategorySelect(category)}
                    className={`px-3 py-2 hover:bg-gray-100 cursor-pointer ${
                      message.category === category ? 'bg-blue-50' : ''
                    }`}
                  >
                    {category}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 ml-2">
          <button onClick={handleToggleSentiment} className="focus:outline-none">
            {message.sentiment === MESSAGE_TYPES.POSITIVE && (
              <Plus className="text-green-500" size={20} />
            )}
            {message.sentiment === MESSAGE_TYPES.NEGATIVE && (
              <Minus className="text-orange-500" size={20} />
            )}
            {message.sentiment === MESSAGE_TYPES.NEUTRAL && (
              <Circle className="text-gray-400" size={20} />
            )}
          </button>
        </div>
      </div>

      {message.note && (
        <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
          {message.note}
        </div>
      )}
      
      {message.attachment && (
        <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
          <Paperclip size={14} />
          <a href={URL.createObjectURL(message.attachment)} 
             target="_blank" 
             rel="noopener noreferrer"
             className="hover:underline">
            {message.attachment.name}
          </a>
        </div>
      )}

      {showActions && (
        <div className="mt-2 flex gap-2 text-xs text-gray-500">
          <button
            onClick={() => setShowNoteInput(true)}
            className="hover:text-gray-700"
          >
            Add Note
          </button>
          {showNoteInput && (
            <form onSubmit={handleNoteSubmit} className="flex gap-2 items-center">
              <input
                type="text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="border rounded px-2 py-1"
                placeholder="Enter note..."
                autoFocus
              />
              <button
                type="submit"
                className="text-blue-500 hover:text-blue-700"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setShowNoteInput(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={14} />
              </button>
            </form>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="hover:text-gray-700"
          >
            <Paperclip size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageCard;